import { type BaseTicketComment } from "@mg/schemas/src/christo/catalyst";
import { CaretLeft, CaretRight } from "@phosphor-icons/react";
import { Badge, Button, Flex, Spinner, Text } from "@radix-ui/themes";
import { getDocument } from "pdfjs-dist";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";

import { ImageCommentPin } from "./ImageCommentPin";

import { renderCommentAvatar } from "../../../utils/helpers/ai-persona-avatars";

interface AssetFile {
  source: string;
  name?: string;
  contentType?: string;
}

interface HighlightPosition {
  x: number;
  y: number;
  page?: number;
}

interface AssetViewerPaneProps {
  title: string;
  assets: AssetFile[];
  highlightPosition?: HighlightPosition | null;
  reviewStatus?: string | null;
  comments?: BaseTicketComment[];
  activeCommentId?: string | null;
  onCommentClick?: (comment: BaseTicketComment) => void;
  onPinClick?: (comment: BaseTicketComment) => void;
  onCitationClick?: (pageNumber: number) => void;
  onCollapse?: () => void;
}

export interface AssetViewerPaneHandle {
  navigateToPage: (page: number) => void;
}

function isPdf(asset: AssetFile): boolean {
  if (asset.contentType?.includes("pdf")) return true;
  if (asset.name?.toLowerCase().endsWith(".pdf")) return true;
  if (asset.source?.toLowerCase().endsWith(".pdf")) return true;
  return false;
}

function isImage(asset: AssetFile): boolean {
  if (asset.contentType?.startsWith("image/")) return true;
  const ext = (asset.name ?? asset.source).toLowerCase();
  return /\.(jpg|jpeg|png|gif|webp|svg|bmp)(\?|$)/.test(ext);
}

function isOfficeFile(asset: AssetFile): boolean {
  const ext = (asset.name ?? asset.source).toLowerCase();
  if (/\.(ppt|pptx|doc|docx|xls|xlsx)(\?|$)/.test(ext)) return true;
  const ct = asset.contentType ?? "";
  return (
    ct.includes("presentationml") ||
    ct.includes("wordprocessingml") ||
    ct.includes("spreadsheetml") ||
    ct.includes("msword") ||
    ct.includes("ms-powerpoint") ||
    ct.includes("ms-excel")
  );
}

function clampZoom(z: number): number {
  return Math.min(Math.max(z, 0.25), 4);
}

/** Horizontal scrolling comment strip below artwork */
function CommentStrip({
  comments,
  activeCommentId,
  onCommentClick,
  onCitationClick,
}: {
  comments: BaseTicketComment[];
  activeCommentId?: string | null;
  onCommentClick?: (comment: BaseTicketComment) => void;
  onCitationClick?: (pageNumber: number) => void;
}) {
  const stripRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!activeCommentId || !stripRef.current) return;
    const el = document.getElementById(`comment-${activeCommentId}`);
    el?.scrollIntoView({
      behavior: "smooth",
      inline: "center",
      block: "nearest",
    });
  }, [activeCommentId]);

  if (comments.length === 0) return null;

  return (
    <div
      data-testid="asset-viewer-comment-strip"
      ref={stripRef}
      className="flex shrink-0 gap-2 overflow-x-auto border-t border-puntt-neutral-gray-5 bg-puntt-neutral-gray-2 px-3 py-2"
    >
      {comments.map((comment) => {
        const isActive = comment._id === activeCommentId;
        const fileCitations = (
          Array.isArray(comment.citations) ? comment.citations : []
        ).filter(
          (
            c,
          ): c is { url: string; pageNumber?: number; originalURL?: string } =>
            "originalURL" in c,
        );
        return (
          <button
            key={comment._id}
            type="button"
            id={`comment-${comment._id}`}
            onClick={() => onCommentClick?.(comment)}
            className={`flex w-[300px] shrink-0 cursor-pointer gap-2 rounded-md border p-2 text-left transition-colors ${
              isActive
                ? "border-puntt-accent-9 bg-puntt-accent-2"
                : "border-puntt-neutral-gray-5 bg-base-white hover:border-puntt-neutral-gray-7"
            }`}
          >
            <span className="mt-0.5 flex size-5 shrink-0 items-center justify-center [&_img]:size-5 [&_svg]:size-5">
              {renderCommentAvatar(comment, true)}
            </span>
            <div className="min-w-0 flex-1">
              <div className="mb-1 flex items-center gap-1">
                {comment.rule && (
                  <Badge size="1" variant="soft" color="gray">
                    {comment.rule}
                  </Badge>
                )}
                {comment.isRequired && (
                  <Badge size="1" variant="soft" color="red">
                    Required
                  </Badge>
                )}
              </div>
              <Text size="1" className="text-puntt-neutral-gray-11">
                {comment.description}
              </Text>
              {fileCitations.length > 0 && (
                <div className="mt-1.5 flex flex-wrap gap-1">
                  {fileCitations.map((citation, idx) => (
                    <span
                      key={idx}
                      role="link"
                      tabIndex={0}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (citation.pageNumber) {
                          onCitationClick?.(citation.pageNumber);
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.stopPropagation();
                          if (citation.pageNumber) {
                            onCitationClick?.(citation.pageNumber);
                          }
                        }
                      }}
                      className="inline-flex cursor-pointer items-center rounded bg-puntt-accent-3 px-1.5 py-0.5 text-xs font-medium text-puntt-accent-11 hover:bg-puntt-accent-4"
                    >
                      Source
                      {citation.pageNumber
                        ? ` (p. ${citation.pageNumber})`
                        : ""}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </button>
        );
      })}
    </div>
  );
}

/** Zoom controls overlay */
function ZoomControls({
  zoom,
  onZoomIn,
  onZoomOut,
  onReset,
}: {
  zoom: number;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onReset: () => void;
}) {
  return (
    <div className="absolute right-2 top-2 z-30 flex items-center gap-1 rounded-md border border-puntt-neutral-gray-5 bg-base-white/90 px-1.5 py-1 shadow-sm backdrop-blur-sm">
      <button
        type="button"
        onClick={onZoomOut}
        className="flex size-5 items-center justify-center rounded text-sm text-puntt-neutral-gray-11 hover:bg-puntt-neutral-gray-3"
        aria-label="Zoom out"
      >
        −
      </button>
      <button
        type="button"
        onClick={onReset}
        className="min-w-[3rem] text-center text-xs text-puntt-neutral-gray-11 hover:text-puntt-neutral-gray-12"
        aria-label="Reset zoom"
      >
        {Math.round(zoom * 100)}%
      </button>
      <button
        type="button"
        onClick={onZoomIn}
        className="flex size-5 items-center justify-center rounded text-sm text-puntt-neutral-gray-11 hover:bg-puntt-neutral-gray-3"
        aria-label="Zoom in"
      >
        +
      </button>
    </div>
  );
}

/** Office file viewer via Microsoft public embed */
function OfficeViewer({ url }: { url: string }) {
  return (
    <iframe
      src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}`}
      className="h-full w-full border-0"
      title="Office document preview"
    />
  );
}

/** Full-pane PDF renderer with page navigation, highlight overlay, and comment pins */
function PdfViewer({
  url,
  highlightPosition,
  onPageChange,
  comments,
  activeCommentId,
  onPinClick,
}: {
  url: string;
  highlightPosition?: HighlightPosition | null;
  onPageChange?: (setter: (page: number) => void) => void;
  comments?: BaseTicketComment[];
  activeCommentId?: string | null;
  onPinClick?: (comment: BaseTicketComment) => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [rendering, setRendering] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [zoom, setZoom] = useState(1);
  const pdfDocRef = useRef<Awaited<
    ReturnType<typeof getDocument>["promise"]
  > | null>(null);
  const totalPagesRef = useRef(0);
  totalPagesRef.current = totalPages;

  // Expose page navigation to parent
  useEffect(() => {
    onPageChange?.((page: number) => {
      if (page >= 1 && page <= totalPagesRef.current) {
        setCurrentPage(page);
      }
    });
  }, [onPageChange]);

  const renderPage = useCallback(async (pageNum: number) => {
    const pdfDoc = pdfDocRef.current;
    const canvas = canvasRef.current;
    if (!pdfDoc || !canvas) return;

    setRendering(true);
    try {
      const page = await pdfDoc.getPage(pageNum);
      const scale = 1.5;
      const viewport = page.getViewport({ scale });

      canvas.width = viewport.width;
      canvas.height = viewport.height;

      const context = canvas.getContext("2d");
      if (!context) return;

      await page.render({ canvasContext: context, viewport }).promise;
    } catch (err) {
      console.error("Error rendering PDF page:", err);
      setError("Failed to render page");
    } finally {
      setRendering(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function loadPdf() {
      try {
        const pdfDoc = await getDocument(url).promise;
        if (cancelled) return;
        pdfDocRef.current = pdfDoc;
        setTotalPages(pdfDoc.numPages);
        await renderPage(1);
      } catch (err) {
        if (cancelled) return;
        console.error("Error loading PDF:", err);
        setError("Failed to load PDF");
        setRendering(false);
      }
    }

    loadPdf();
    return () => {
      cancelled = true;
    };
  }, [url, renderPage]);

  useEffect(() => {
    if (pdfDocRef.current && currentPage > 0) {
      renderPage(currentPage);
    }
  }, [currentPage, renderPage]);

  // Navigate to highlight page when position changes
  useEffect(() => {
    if (highlightPosition?.page && highlightPosition.page !== currentPage) {
      setCurrentPage(highlightPosition.page);
    }
  }, [highlightPosition]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset zoom when URL changes
  useEffect(() => {
    setZoom(1);
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0;
      scrollRef.current.scrollLeft = 0;
    }
  }, [url]);

  const pageComments = (comments ?? []).filter(
    (c) => (c.page_num ?? 1) === currentPage,
  );

  if (error) {
    return (
      <div className="flex h-full items-center justify-center">
        <Text size="2" className="text-puntt-neutral-gray-9">
          {error}
        </Text>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      <div
        ref={scrollRef}
        className="relative flex-1 overflow-auto bg-puntt-neutral-gray-3 p-2"
        onWheel={(e) => {
          e.preventDefault();
          setZoom((z) => clampZoom(z * (e.deltaY < 0 ? 1.15 : 1 / 1.15)));
        }}
      >
        {rendering && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-puntt-neutral-gray-3/80">
            <Spinner size="2" />
          </div>
        )}
        <ZoomControls
          zoom={zoom}
          onZoomIn={() => setZoom((z) => clampZoom(z * 1.15))}
          onZoomOut={() => setZoom((z) => clampZoom(z / 1.15))}
          onReset={() => {
            setZoom(1);
            if (scrollRef.current) {
              scrollRef.current.scrollTop = 0;
              scrollRef.current.scrollLeft = 0;
            }
          }}
        />
        <div
          className="relative inline-block"
          style={{ transform: `scale(${zoom})`, transformOrigin: "top left" }}
        >
          <canvas
            ref={canvasRef}
            className="mx-auto block"
            style={{ maxWidth: "100%" }}
          />
          {/* Comment pins */}
          {pageComments.map((comment) => (
            <ImageCommentPin
              key={comment._id}
              comment={comment}
              isActive={comment._id === activeCommentId}
              onPinClick={(c) => onPinClick?.(c)}
            />
          ))}
          {/* Highlight marker */}
          {highlightPosition &&
            (!highlightPosition.page ||
              highlightPosition.page === currentPage) && (
              <div
                className="pointer-events-none absolute z-20 size-6 -translate-x-1/2 -translate-y-1/2 animate-ping rounded-full border-2 border-puntt-accent-9 bg-puntt-accent-9/30"
                style={{
                  left: `${highlightPosition.x * 100}%`,
                  top: `${highlightPosition.y * 100}%`,
                }}
              />
            )}
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <Flex
          align="center"
          justify="center"
          gap="2"
          className="shrink-0 border-t border-puntt-neutral-gray-5 bg-base-white px-3 py-2"
        >
          <Button
            data-testid="asset-viewer-prev-page"
            size="1"
            variant="ghost"
            disabled={currentPage <= 1}
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            className="cursor-pointer"
          >
            <CaretLeft size={14} />
          </Button>
          <Text
            data-testid="asset-viewer-page-indicator"
            size="1"
            className="text-puntt-neutral-gray-11"
          >
            Page {currentPage} of {totalPages}
          </Text>
          <Button
            data-testid="asset-viewer-next-page"
            size="1"
            variant="ghost"
            disabled={currentPage >= totalPages}
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            className="cursor-pointer"
          >
            <CaretRight size={14} />
          </Button>
        </Flex>
      )}
    </div>
  );
}

/** Image viewer with highlight overlay, comment pins, and pagination */
function ImageViewer({
  assets,
  highlightPosition,
  comments,
  activeCommentId,
  onPinClick,
  onPageChange,
}: {
  assets: AssetFile[];
  highlightPosition?: HighlightPosition | null;
  comments?: BaseTicketComment[];
  activeCommentId?: string | null;
  onPinClick?: (comment: BaseTicketComment) => void;
  onPageChange?: (setter: (page: number) => void) => void;
}) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [zoom, setZoom] = useState(1);
  const scrollRef = useRef<HTMLDivElement>(null);
  const totalPages = assets.length;
  const asset = assets[currentIndex] ?? assets[0];
  const totalPagesRef = useRef(totalPages);
  totalPagesRef.current = totalPages;

  // Expose page navigation to parent
  useEffect(() => {
    onPageChange?.((page: number) => {
      if (page >= 1 && page <= totalPagesRef.current) {
        setCurrentIndex(page - 1);
      }
    });
  }, [onPageChange]);

  // Navigate to correct page when a pin is clicked on a different page
  useEffect(() => {
    if (
      highlightPosition?.page &&
      highlightPosition.page !== currentIndex + 1
    ) {
      setCurrentIndex(
        Math.min(Math.max(highlightPosition.page - 1, 0), totalPages - 1),
      );
    }
  }, [highlightPosition]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset zoom when asset or page changes
  useEffect(() => {
    setZoom(1);
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0;
      scrollRef.current.scrollLeft = 0;
    }
  }, [assets, currentIndex]);

  const pageComments = (comments ?? []).filter(
    (c) => (c.page_num ?? 1) === currentIndex + 1,
  );

  return (
    <div className="flex h-full flex-col">
      <div
        ref={scrollRef}
        className="relative flex-1 overflow-auto bg-puntt-neutral-gray-3 p-2 text-center"
        onWheel={(e) => {
          e.preventDefault();
          setZoom((z) => clampZoom(z * (e.deltaY < 0 ? 1.15 : 1 / 1.15)));
        }}
      >
        <ZoomControls
          zoom={zoom}
          onZoomIn={() => setZoom((z) => clampZoom(z * 1.15))}
          onZoomOut={() => setZoom((z) => clampZoom(z / 1.15))}
          onReset={() => {
            setZoom(1);
            if (scrollRef.current) {
              scrollRef.current.scrollTop = 0;
              scrollRef.current.scrollLeft = 0;
            }
          }}
        />
        <div
          className="relative inline-block"
          style={{ transform: `scale(${zoom})`, transformOrigin: "top left" }}
        >
          <img
            src={asset.source}
            alt={asset.name ?? `Image ${currentIndex + 1}`}
            className={`block object-contain ${zoom > 1 ? "" : "max-w-full"}`}
          />
          {/* Comment pins */}
          {pageComments.map((comment) => (
            <ImageCommentPin
              key={comment._id}
              comment={comment}
              isActive={comment._id === activeCommentId}
              onPinClick={(c) => onPinClick?.(c)}
            />
          ))}
          {highlightPosition &&
            (!highlightPosition.page ||
              highlightPosition.page === currentIndex + 1) && (
              <div
                className="pointer-events-none absolute z-20 size-6 -translate-x-1/2 -translate-y-1/2 animate-ping rounded-full border-2 border-puntt-accent-9 bg-puntt-accent-9/30"
                style={{
                  left: `${highlightPosition.x * 100}%`,
                  top: `${highlightPosition.y * 100}%`,
                }}
              />
            )}
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <Flex
          align="center"
          justify="center"
          gap="2"
          className="shrink-0 border-t border-puntt-neutral-gray-5 bg-base-white px-3 py-2"
        >
          <Button
            data-testid="asset-viewer-prev-page"
            size="1"
            variant="ghost"
            disabled={currentIndex <= 0}
            onClick={() => setCurrentIndex((p) => Math.max(0, p - 1))}
            className="cursor-pointer"
          >
            <CaretLeft size={14} />
          </Button>
          <Text
            data-testid="asset-viewer-page-indicator"
            size="1"
            className="text-puntt-neutral-gray-11"
          >
            Page {currentIndex + 1} of {totalPages}
          </Text>
          <Button
            data-testid="asset-viewer-next-page"
            size="1"
            variant="ghost"
            disabled={currentIndex >= totalPages - 1}
            onClick={() =>
              setCurrentIndex((p) => Math.min(totalPages - 1, p + 1))
            }
            className="cursor-pointer"
          >
            <CaretRight size={14} />
          </Button>
        </Flex>
      )}
    </div>
  );
}

export const AssetViewerPane = forwardRef<
  AssetViewerPaneHandle,
  AssetViewerPaneProps
>(function AssetViewerPane(
  {
    title,
    assets,
    highlightPosition,
    reviewStatus,
    comments,
    activeCommentId,
    onCommentClick,
    onPinClick,
    onCitationClick,
    onCollapse,
  },
  ref,
) {
  const [activeTab, setActiveTab] = useState(0);
  const pageNavigatorRef = useRef<((page: number) => void) | null>(null);

  useImperativeHandle(ref, () => ({
    navigateToPage(page: number) {
      pageNavigatorRef.current?.(page);
    },
  }));

  if (assets.length === 0) {
    return (
      <div className="flex h-full items-center justify-center bg-base-white">
        <Text size="2" className="text-puntt-neutral-gray-9">
          No {title.toLowerCase()} files
        </Text>
      </div>
    );
  }

  const currentAsset = assets[activeTab] ?? assets[0];
  const allImages = assets.length > 1 && assets.every((a) => isImage(a));
  const showTabs = assets.length > 1 && !allImages;
  const hasComments = comments && comments.length > 0;

  return (
    <div
      data-testid="asset-viewer-pane"
      className="flex h-full flex-col bg-base-white"
    >
      {/* Header with title and optional tabs */}
      <div className="shrink-0 border-b border-puntt-neutral-gray-5 px-4 py-2">
        <Flex align="center" gap="2" className="mb-1">
          <Text
            size="1"
            weight="bold"
            className="block uppercase tracking-wide text-puntt-neutral-gray-9"
          >
            {title}
          </Text>
          {onCollapse && (
            <button
              onClick={onCollapse}
              className="ml-auto flex size-5 items-center justify-center rounded text-puntt-neutral-gray-9 transition-colors hover:bg-puntt-neutral-gray-3 hover:text-puntt-neutral-gray-12"
              aria-label={`Collapse ${title} pane`}
            >
              <CaretLeft size={12} />
            </button>
          )}
        </Flex>
        {reviewStatus && (
          <Text size="1" className="animate-pulse italic text-puntt-accent-9">
            {reviewStatus}
          </Text>
        )}
        {showTabs && (
          <Flex gap="1" className="overflow-x-auto">
            {assets.map((asset, i) => (
              <button
                key={i}
                data-testid="asset-viewer-tab"
                onClick={() => setActiveTab(i)}
                className={`rounded px-2 py-0.5 text-xs transition-colors ${
                  i === activeTab
                    ? "bg-puntt-accent-3 font-medium text-puntt-accent-11"
                    : "text-puntt-neutral-gray-9 hover:bg-puntt-neutral-gray-3"
                }`}
              >
                {asset.name ?? `File ${i + 1}`}
              </button>
            ))}
          </Flex>
        )}
      </div>

      {/* Content */}
      <div className="min-h-0 flex-1">
        {allImages ? (
          <ImageViewer
            assets={assets}
            highlightPosition={highlightPosition}
            onPageChange={(setter) => {
              pageNavigatorRef.current = setter;
            }}
            comments={comments}
            activeCommentId={activeCommentId}
            onPinClick={onPinClick}
          />
        ) : isPdf(currentAsset) ? (
          <PdfViewer
            key={currentAsset.source}
            url={currentAsset.source}
            highlightPosition={highlightPosition}
            onPageChange={(setter) => {
              pageNavigatorRef.current = setter;
            }}
            comments={comments}
            activeCommentId={activeCommentId}
            onPinClick={onPinClick}
          />
        ) : isImage(currentAsset) ? (
          <ImageViewer
            assets={[currentAsset]}
            highlightPosition={highlightPosition}
            onPageChange={(setter) => {
              pageNavigatorRef.current = setter;
            }}
            comments={comments}
            activeCommentId={activeCommentId}
            onPinClick={onPinClick}
          />
        ) : isOfficeFile(currentAsset) ? (
          <OfficeViewer url={currentAsset.source} />
        ) : (
          // Fallback: try rendering as image
          <ImageViewer
            assets={[currentAsset]}
            highlightPosition={highlightPosition}
            onPageChange={(setter) => {
              pageNavigatorRef.current = setter;
            }}
            comments={comments}
            activeCommentId={activeCommentId}
            onPinClick={onPinClick}
          />
        )}
      </div>

      {/* Horizontal comment strip */}
      {hasComments && (
        <CommentStrip
          comments={comments}
          activeCommentId={activeCommentId}
          onCommentClick={onCommentClick}
          onCitationClick={onCitationClick}
        />
      )}
    </div>
  );
});
